import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  ChevronUp,
  ChevronDown,
  X,
  Mail,
  Link as LinkIcon,
  Copy,
  Share2,
} from 'lucide-react';

// --- Helper Functions ---
const getDecade = (dateStr) => {
  if (!dateStr || dateStr === 'Unknown' || dateStr.length < 4) {
    return 'Unknown';
  }
  const year = parseInt(dateStr.substring(0, 4), 10);
  if (isNaN(year)) {
    return 'Unknown';
  }
  return `${Math.floor(year / 10) * 10}s`;
};

// --- Sub-Components ---

const LetterCard = ({ letter, onSelect, selectedLanguage }) => {
  const textToShow =
    letter.text[selectedLanguage] ||
    letter.text['English'] ||
    Object.values(letter.text)[0];
  const snippet = textToShow
    ? textToShow.substring(0, 150) + '...'
    : 'No text available in this language.';

  return (
    <div
      className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-6 border border-gray-200 hover:shadow-xl hover:border-blue-300 transition-all duration-300 cursor-pointer flex flex-col justify-between h-full"
      onClick={() => onSelect(letter)}
    >
      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-800 font-serif">
            Dated: {letter.date}
          </h3>
        </div>
        <div className="text-sm text-gray-600 mb-4 space-y-1">
          <p>
            <span className="font-semibold">From:</span> {letter.from}
          </p>
          <p>
            <span className="font-semibold">To:</span> {letter.to}
          </p>
        </div>
        <p className="text-gray-700 text-sm leading-relaxed">{snippet}</p>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onSelect(letter);
        }}
        className="mt-4 text-blue-600 hover:text-blue-800 font-semibold text-sm self-start"
      >
        Read Full Letter
      </button>
    </div>
  );
};

const LetterModal = ({
  letter,
  onClose,
  selectedLanguage,
  onLanguageChange,
}) => {
  const [copyStatus, setCopyStatus] = useState({
    link: 'Copy Link',
    text: 'Copy Text',
  });
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(true);

  useEffect(() => {
    if (letter) {
      setIsHeaderExpanded(true);
    }
  }, [letter]);

  if (!letter) return null;

  const textToShow =
    letter.text[selectedLanguage] ||
    letter.text['English'] ||
    Object.values(letter.text)[0];
  const availableLanguages = Object.keys(letter.text);

  const copyToClipboard = (text, type) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      setCopyStatus((prev) => ({ ...prev, [type]: 'Copied!' }));
      setTimeout(
        () =>
          setCopyStatus((prev) => ({
            ...prev,
            [type]: type === 'link' ? 'Copy Link' : 'Copy Text',
          })),
        2000
      );
    } catch (err) {
      setCopyStatus((prev) => ({ ...prev, [type]: 'Failed!' }));
      console.error(`Failed to copy ${type}: `, err);
    }
    document.body.removeChild(textArea);
  };

  const handleCopyLink = () => {
    try {
      const url = new URL(window.location);
      url.searchParams.set('letter', letter.letter_id);
      copyToClipboard(url.toString(), 'link');
    } catch (err) {
      console.error('Could not create URL for copying: ', err);
      setCopyStatus((prev) => ({ ...prev, link: 'Failed!' }));
    }
  };

  const handleCopyText = () => {
    const fullText = `Dated ${letter.date}\nDate: ${letter.date}\nFrom: ${letter.from}\nTo: ${letter.to}\n\n---\n\n${textToShow}`;
    copyToClipboard(fullText, 'text');
  };

  const handleShare = async () => {
    const shareData = {
      title: `Dated ${letter.date} from Dr. Ambedkar's Archive`,
      text: `A letter from ${letter.from} to ${letter.to}, dated ${letter.date}.`,
      url: window.location.href, // The URL should already have the ?letter=ID
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        alert('Share feature is not supported on this browser.');
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 transition-opacity"
      onClick={onClose}
    >
      <div
        className="bg-stone-50 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-200 flex justify-between items-center gap-2 flex-wrap">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 font-serif">
            Letter {letter.date}
          </h2>
          <div className="flex items-center gap-1 md:gap-3">
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-1.5 text-xs md:text-sm text-blue-600 hover:text-blue-800 font-semibold transition-colors p-1 rounded-md hover:bg-blue-50"
            >
              <LinkIcon size={16} />
              <span className="hidden sm:inline">{copyStatus.link}</span>
            </button>
            <button
              onClick={handleCopyText}
              className="flex items-center gap-1.5 text-xs md:text-sm text-blue-600 hover:text-blue-800 font-semibold transition-colors p-1 rounded-md hover:bg-blue-50"
            >
              <Copy size={16} />
              <span className="hidden sm:inline">{copyStatus.text}</span>
            </button>
            {navigator.share && (
              <button
                onClick={handleShare}
                className="flex items-center gap-1.5 text-xs md:text-sm text-blue-600 hover:text-blue-800 font-semibold transition-colors p-1 rounded-md hover:bg-blue-50"
              >
                <Share2 size={16} />
                <span className="hidden sm:inline">Share</span>
              </button>
            )}
            <button
              onClick={() => setIsHeaderExpanded(!isHeaderExpanded)}
              className="p-1 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-800"
            >
              {isHeaderExpanded ? (
                <ChevronUp size={20} />
              ) : (
                <ChevronDown size={20} />
              )}
            </button>
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-800"
            >
              <X size={24} />
            </button>
          </div>
        </div>
        <div
          className={`flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden ${
            isHeaderExpanded ? 'max-h-[500px]' : 'max-h-0'
          }`}
        >
          <div className="p-6 text-sm text-gray-600 bg-stone-100 border-b border-gray-200">
            <p>
              <span className="font-semibold text-gray-800">Date:</span>{' '}
              {letter.date}
            </p>
            <p>
              <span className="font-semibold text-gray-800">From:</span>{' '}
              {letter.from}
            </p>
            <p>
              <span className="font-semibold text-gray-800">To:</span>{' '}
              {letter.to}
            </p>
            <div className="mt-2 flex items-center gap-2">
              <label
                htmlFor="language-select-modal"
                className="font-semibold text-gray-800"
              >
                Language:
              </label>
              <select
                id="language-select-modal"
                value={selectedLanguage}
                onChange={(e) => onLanguageChange(e.target.value)}
                className="p-1 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                {availableLanguages.map((lang) => (
                  <option key={lang} value={lang}>
                    {lang}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="flex-grow overflow-hidden flex flex-col">
          <div className="p-6 overflow-y-auto">
            <p className="text-gray-800 whitespace-pre-wrap leading-loose font-serif">
              {textToShow}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const FilterControls = ({
  filters,
  setFilters,
  decades,
  senders,
  recipients,
}) => {
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="bg-white/70 backdrop-blur-md p-6 rounded-xl shadow-md mb-8 border border-gray-200">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Basic Filters */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Language
          </label>
          <select
            name="selectedLanguage"
            value={filters.selectedLanguage}
            onChange={handleInputChange}
            className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="English">English</option>
            <option value="Marathi">Marathi</option>
            <option value="Tamil">Tamil</option>
          </select>
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sort by Date
          </label>
          <select
            name="sortOrder"
            value={filters.sortOrder}
            onChange={handleInputChange}
            className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="date-asc">Oldest First</option>
            <option value="date-desc">Newest First</option>
          </select>
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Decade
          </label>
          <select
            name="selectedDecade"
            value={filters.selectedDecade}
            onChange={handleInputChange}
            className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Decades</option>
            {decades.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        {/* Full-Text Search */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search Letter Content
          </label>
          <div className="relative">
            <input
              type="text"
              name="searchTermText"
              placeholder="Search text (min 3 chars)..."
              value={filters.searchTermText}
              onChange={handleInputChange}
              className="w-full p-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        {/* Correspondent Filters */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            From
          </label>
          <div className="relative">
            <input
              type="text"
              name="searchTermFrom"
              placeholder="Search sender..."
              value={filters.searchTermFrom}
              onChange={handleInputChange}
              className="w-full p-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              list="senders-list"
            />
            <datalist id="senders-list">
              {senders.map((sender) => (
                <option key={sender} value={sender} />
              ))}
            </datalist>
          </div>
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            To
          </label>
          <div className="relative">
            <input
              type="text"
              name="searchTermTo"
              placeholder="Search recipient..."
              value={filters.searchTermTo}
              onChange={handleInputChange}
              className="w-full p-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              list="recipients-list"
            />
            <datalist id="recipients-list">
              {recipients.map((recipient) => (
                <option key={recipient} value={recipient} />
              ))}
            </datalist>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Main Archive Component ---
const LetterArchive = () => {
  {
    /*
      HIGHLIGHT START
      The static import of `letterData` is removed.
      Instead, we use state to hold the letter data, initializing it as an empty array.
    */
  }
  const [letterData, setLetterData] = useState([]);
  const [filteredLetters, setFilteredLetters] = useState([]);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [filters, setFilters] = useState({
    selectedLanguage: 'English',
    selectedDecade: 'all',
    searchTermFrom: '',
    searchTermTo: '',
    searchTermText: '',
    sortOrder: 'date-asc',
  });

  {
    /*
      This `useEffect` hook runs only once on component mount (due to the empty dependency array `[]`).
      It uses a dynamic import to fetch the JSON data asynchronously.
      Once fetched, the data is stored in the `letterData` state.
    */
  }
  useEffect(() => {
    const fetchLetterData = async () => {
      try {
        const data = await import('../asset/letters.json');
        // The default export from the JSON module is the data itself
        setLetterData(data.default);
      } catch (error) {
        console.error('Failed to load letter data:', error);
      }
    };
    document.title = 'The Letters of Dr. B.R. Ambedkar - Archive';
    fetchLetterData();
  }, []);
  {
    /* HIGHLIGHT END */
  }

  const handleSelectLetter = (letter) => {
    setSelectedLetter(letter);
    try {
      const url = new URL(window.location);
      if (letter) {
        url.searchParams.set('letter', letter.letter_id);
      } else {
        url.searchParams.delete('letter');
      }
      window.history.replaceState({}, '', url.toString());
    } catch (error) {
      console.warn(
        'Could not update URL. This is expected in some sandboxed environments.',
        error
      );
    }
  };

  {
    /*
    HIGHLIGHT START
    The `useMemo` hook now depends on `letterData`. This ensures that the decades, senders,
    and recipients are only calculated after the letter data has been loaded.
  */
  }
  const { decades, senders, recipients } = useMemo(() => {
    if (!letterData || letterData.length === 0) {
      return { decades: [], senders: [], recipients: [] };
    }
    const decadeSet = new Set();
    const senderSet = new Set();
    const recipientSet = new Set();

    letterData.forEach((letter) => {
      decadeSet.add(getDecade(letter.date));
      senderSet.add(letter.from);
      recipientSet.add(letter.to);
    });

    return {
      decades: Array.from(decadeSet).sort(),
      senders: Array.from(senderSet).sort(),
      recipients: Array.from(recipientSet).sort(),
    };
  }, [letterData]);
  {
    /* HIGHLIGHT END */
  }

  useEffect(() => {
    if (!letterData || letterData.length === 0) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const letterId = params.get('letter');
      if (letterId) {
        const letterToOpen = letterData.find(
          (l) => l.letter_id.toString() === letterId
        );
        if (letterToOpen) {
          setSelectedLetter(letterToOpen);
        }
      }
    } catch (error) {
      console.warn('Could not read URL parameters.', error);
    }
  }, [letterData]); // Depends on letterData to ensure it runs after data is available

  {
    /*
    HIGHLIGHT START
    The main filtering effect now also depends on `letterData`. This prevents it from
    running with an empty dataset before the data has been loaded.
  */
  }
  useEffect(() => {
    // Don't filter until the data has been loaded.
    if (!letterData || letterData.length === 0) {
      setFilteredLetters([]);
      return;
    }

    let currentLetters = [...letterData];

    if (filters.selectedLanguage) {
      currentLetters = currentLetters.filter((letter) =>
        Object.keys(letter.text).includes(filters.selectedLanguage)
      );
    }

    const searchTerm = filters.searchTermText.toLowerCase();
    if (searchTerm.length >= 3) {
      currentLetters = currentLetters.filter((letter) => {
        const textInSelectedLang = letter.text[filters.selectedLanguage];
        return (
          textInSelectedLang &&
          textInSelectedLang.toLowerCase().includes(searchTerm)
        );
      });
    }

    if (filters.selectedDecade !== 'all') {
      currentLetters = currentLetters.filter(
        (letter) => getDecade(letter.date) === filters.selectedDecade
      );
    }

    if (filters.searchTermFrom) {
      currentLetters = currentLetters.filter((letter) =>
        letter.from.toLowerCase().includes(filters.searchTermFrom.toLowerCase())
      );
    }

    if (filters.searchTermTo) {
      currentLetters = currentLetters.filter((letter) =>
        letter.to.toLowerCase().includes(filters.searchTermTo.toLowerCase())
      );
    }

    currentLetters.sort((a, b) => {
      const dateA = a.date === 'Unknown' ? null : new Date(a.date);
      const dateB = b.date === 'Unknown' ? null : new Date(b.date);

      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      if (filters.sortOrder === 'date-asc') {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });

    setFilteredLetters(currentLetters);
  }, [filters, letterData]);
  {
    /* HIGHLIGHT END */
  }

  return (
    <div className="min-h-screen bg-stone-100 font-sans text-gray-800">
      <div className="container mx-auto p-4 md:p-8">
        <header className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 font-serif tracking-tight">
            The Letters of Dr. B.R. Ambedkar
          </h1>
          <p className="mt-3 text-lg text-gray-600">An Interactive Archive</p>
        </header>

        <FilterControls
          filters={filters}
          setFilters={setFilters}
          decades={decades}
          senders={senders}
          recipients={recipients}
        />

        {filteredLetters.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredLetters.map((letter) => (
              <LetterCard
                key={letter.letter_id}
                letter={letter}
                onSelect={handleSelectLetter}
                selectedLanguage={filters.selectedLanguage}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white/50 rounded-lg shadow">
            <Mail size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-xl text-gray-600">
              {letterData.length === 0
                ? 'Loading letters...'
                : 'No letters match your current filters.'}
            </p>
            {letterData.length > 0 && (
              <p className="text-sm text-gray-500 mt-2">
                Try adjusting your search criteria.
              </p>
            )}
          </div>
        )}

        <LetterModal
          letter={selectedLetter}
          onClose={() => handleSelectLetter(null)}
          selectedLanguage={filters.selectedLanguage}
          onLanguageChange={(lang) =>
            setFilters((prev) => ({ ...prev, selectedLanguage: lang }))
          }
        />

        <footer className="text-center mt-16 text-gray-500 text-sm">
          <p>
            This archive is presented with the utmost respect for Dr. B.R.
            Ambedkar and his correspondents.
          </p>
          <p>Data sourced from ambedkarletters.com.</p>
        </footer>
      </div>
    </div>
  );
};

export default LetterArchive;
